/* ═══════════════════════════════════════════════════════════════════════
   pb-widgets.js — comportement des briques interactives du PageBuilder.

   Va avec pb-widgets.css. Chaque brique est déjà lisible sans ce fichier
   (panneaux d'onglets empilés, compteur sur sa valeur finale, barre pleine,
   avant/après en demi-écran) : le runtime n'ajoute que l'interaction, il ne
   fabrique aucun contenu. Conséquence utile : le canvas de l'éditeur, où ce
   script ne tourne pas, montre tout et reste éditable.

   Idempotent (marque data-pbw-live) et re-scanné via MutationObserver — les
   pages sous circuit Blazor et les îlots injectés après coup sont couverts.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';

    if (!document.body) return;

    function once(el) {
        if (el.getAttribute('data-pbw-live') === '1') return false;
        el.setAttribute('data-pbw-live', '1');
        return true;
    }

    // ── Onglets ──────────────────────────────────────────────────────────
    function initTabs(root) {
        if (!once(root)) return;
        var tabs = [].slice.call(root.querySelectorAll('.pb-tabs-tab'));
        var panels = [].slice.call(root.querySelectorAll('.pb-tabs-panel'));
        if (!tabs.length || !panels.length) return;

        root.classList.add('pb-tabs--live');
        var nav = root.querySelector('.pb-tabs-nav');
        if (nav) nav.setAttribute('role', 'tablist');

        function select(i) {
            tabs.forEach(function (t, n) {
                t.setAttribute('role', 'tab');
                t.setAttribute('aria-selected', n === i ? 'true' : 'false');
                t.setAttribute('tabindex', n === i ? '0' : '-1');
            });
            panels.forEach(function (p, n) {
                p.setAttribute('role', 'tabpanel');
                p.hidden = n !== i;
            });
        }

        tabs.forEach(function (t, i) {
            t.addEventListener('click', function () { select(i); });
            t.addEventListener('keydown', function (e) {
                var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                if (!d) return;
                e.preventDefault();
                var next = (i + d + tabs.length) % tabs.length;
                select(next);
                tabs[next].focus();
            });
        });

        // Onglet actif au chargement : celui marqué dans le HTML, sinon le premier.
        var initial = tabs.findIndex ? tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; }) : -1;
        var current = initial > -1 ? initial : 0;
        select(current);

        // Avance automatique (data-pbw-autoplay = durée d'un onglet en ms) : sert aux
        // parcours « pas à pas ». Démarre quand le bloc entre dans l'écran, s'arrête
        // définitivement à la première interaction du visiteur (clic, clavier, toucher).
        var delay = parseInt(root.getAttribute('data-pbw-autoplay') || '0', 10);
        if (!(delay > 0) || reducedMotion() || tabs.length < 2) return;
        root.style.setProperty('--pbw-autoplay', delay + 'ms');
        var timer = null;
        function stop() {
            if (timer) { clearInterval(timer); timer = null; }
            root.classList.add('pb-tabs--stopped');
        }
        tabs.forEach(function (t, i) {
            t.addEventListener('click', function () { current = i; stop(); });
            t.addEventListener('keydown', stop);
        });
        root.addEventListener('pointerdown', stop);
        whenVisible(root, function () {
            if (root.classList.contains('pb-tabs--stopped')) return;
            root.classList.add('pb-tabs--auto');
            timer = setInterval(function () {
                current = (current + 1) % tabs.length;
                select(current);
            }, delay);
        });
    }

    function reducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    // ── Points interactifs sur une image ─────────────────────────────────
    // Les points sont une liste ordinaire (.pb-hotspot : titre + texte, position
    // en % dans data-pbw-x / data-pbw-y, zone éclairée « gauche,haut,largeur,
    // hauteur » en % dans data-pbw-zone). Sans ce script, c'est une liste
    // numérotée sous l'image. Le runtime pose les pastilles sur l'image, éclaire
    // la zone du point actif, affiche sa légende et fait défiler les points tant
    // que le visiteur n'a pas pris la main.
    function initHotspots(root) {
        if (!once(root)) return;
        var media = root.querySelector('.pb-hotspots-media');
        var items = [].slice.call(root.querySelectorAll('.pb-hotspot'));
        if (!media || !items.length) return;

        function num(el, name) {
            var n = parseFloat(el.getAttribute(name));
            return isFinite(n) ? n : null;
        }
        function zone(el) {
            var parts = (el.getAttribute('data-pbw-zone') || '').split(/[,; ]+/).map(parseFloat);
            return parts.length === 4 && parts.every(isFinite) ? parts : null;
        }

        root.classList.add('pb-hotspots--live');

        var light = document.createElement('span');
        light.className = 'pb-hotspots-light';
        light.setAttribute('aria-hidden', 'true');
        media.appendChild(light);

        var caption = document.createElement('div');
        caption.className = 'pb-hotspots-caption';
        caption.setAttribute('aria-live', 'polite');
        var capNum = document.createElement('span');
        capNum.className = 'pb-hotspots-caption-num';
        var capBody = document.createElement('div');
        var capTitle = document.createElement('strong');
        capTitle.className = 'pb-hotspots-caption-title';
        var capText = document.createElement('div');
        capText.className = 'pb-hotspots-caption-text';
        capBody.append(capTitle, capText);
        caption.append(capNum, capBody);
        media.insertAdjacentElement('afterend', caption);

        var dots = items.map(function (item, i) {
            var title = item.querySelector('.pb-hotspot-title');
            var label = (title ? title.textContent : item.textContent || '').trim();
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'pb-hotspots-dot';
            dot.textContent = String(i + 1);
            dot.setAttribute('aria-label', (i + 1) + ' · ' + label);
            var x = num(item, 'data-pbw-x'), y = num(item, 'data-pbw-y');
            if (x === null || y === null) dot.hidden = true;
            else { dot.style.left = x + '%'; dot.style.top = y + '%'; }
            media.appendChild(dot);

            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', '0');
            return dot;
        });

        var active = -1;
        var timer = null;
        var touched = false;
        function stop() { touched = true; if (timer) { clearInterval(timer); timer = null; } }

        function activate(i) {
            if (i === active) return;
            active = i;
            var item = items[i];
            items.forEach(function (it, n) {
                var on = n === i;
                it.classList.toggle('is-active', on);
                it.setAttribute('aria-pressed', on ? 'true' : 'false');
                dots[n].classList.toggle('is-active', on);
            });
            var z = zone(item);
            if (z) {
                light.style.left = z[0] + '%';
                light.style.top = z[1] + '%';
                light.style.width = z[2] + '%';
                light.style.height = z[3] + '%';
                light.classList.add('is-on');
            } else {
                light.classList.remove('is-on');
            }
            var title = item.querySelector('.pb-hotspot-title');
            var text = item.querySelector('.pb-hotspot-text');
            capNum.textContent = String(i + 1);
            capTitle.textContent = title ? title.textContent.trim() : '';
            capText.textContent = text ? text.textContent.trim() : '';
            caption.classList.remove('is-fresh');
            void caption.offsetWidth;
            caption.classList.add('is-fresh');
        }

        function take(i) { stop(); activate(i); }
        items.forEach(function (item, i) {
            item.addEventListener('mouseenter', function () { take(i); });
            item.addEventListener('focus', function () { take(i); });
            item.addEventListener('click', function () { take(i); });
            item.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); take(i); }
            });
            dots[i].addEventListener('mouseenter', function () { take(i); });
            dots[i].addEventListener('click', function () { take(i); });
        });

        activate(0);

        var delay = parseInt(root.getAttribute('data-pbw-autoplay') || '0', 10);
        if (delay > 0 && !reducedMotion() && items.length > 1) {
            whenVisible(root, function () {
                if (touched || timer) return;
                timer = setInterval(function () { activate((active + 1) % items.length); }, delay);
            });
            root.addEventListener('pointerdown', stop);
        }
    }

    // ── Liste filtrable par catégories ───────────────────────────────────
    // Les éléments portent leur catégorie dans data-pbw-cat ; les puces sont
    // fabriquées ici (jamais enregistrées) dans l'ordre d'apparition, précédées
    // de « Tout » (data-pbw-all). Sans script : tout est affiché.
    // data-pbw-count (« badge » ou « paren ») ajoute à chaque puce le nombre
    // d'éléments de sa catégorie ; son aspect vient du CSS (--pbw-count-*).
    // Libellés des puces : la catégorie sert à grouper, le texte affiché vient
    // de la liste .pb-filter-labels quand elle existe (un enfant par puce :
    // data-pbw-for="<catégorie>", ou sans data-pbw-for pour « Tout » — le rendu
    // serveur supprime un data-pbw-for-all vide) — du texte de page, donc traduit
    // avec elle (un attribut ne l'est pas). Sans cette liste : la catégorie, comme avant.
    function filterLabels(root) {
        var map = { all: null, cats: {} };
        [].slice.call(root.querySelectorAll('.pb-filter-labels')).forEach(function (box) {
            if (box.closest('.pb-filter') !== root) return;
            [].slice.call(box.children).forEach(function (el) {
                var t = (el.textContent || '').trim();
                if (!t) return;
                if (el.hasAttribute('data-pbw-for')) map.cats[(el.getAttribute('data-pbw-for') || '').trim()] = t;
                else map.all = t;
            });
        });
        return map;
    }

    function initFilter(root) {
        if (!once(root)) return;
        var items = [].slice.call(root.querySelectorAll('[data-pbw-cat]')).filter(function (el) {
            return el.closest('.pb-filter') === root;
        });
        var cats = [];
        items.forEach(function (el) {
            var c = (el.getAttribute('data-pbw-cat') || '').trim();
            if (c && cats.indexOf(c) === -1) cats.push(c);
        });
        if (cats.length < 2) return;

        var bar = root.querySelector('.pb-filter-bar');
        if (!bar) {
            bar = document.createElement('div');
            bar.className = 'pb-filter-bar';
            root.insertBefore(bar, root.firstChild);
        }
        bar.setAttribute('role', 'group');
        root.classList.add('pb-filter--live');

        var labels = filterLabels(root);
        var all = labels.all || root.getAttribute('data-pbw-all') || 'Tout';
        var showCount = !!(root.getAttribute('data-pbw-count') || '').trim();
        var chips = [];
        function apply(cat) {
            items.forEach(function (el) {
                el.hidden = !!cat && (el.getAttribute('data-pbw-cat') || '').trim() !== cat;
            });
            chips.forEach(function (ch) {
                ch.setAttribute('aria-pressed', ch.__pbwCat === cat ? 'true' : 'false');
            });
        }
        [''].concat(cats).forEach(function (cat) {
            var ch = document.createElement('button');
            ch.type = 'button';
            ch.className = 'pb-filter-chip';
            ch.textContent = cat ? (labels.cats[cat] || cat) : all;
            if (showCount) {
                var n = cat ? items.filter(function (el) { return (el.getAttribute('data-pbw-cat') || '').trim() === cat; }).length : items.length;
                var badge = document.createElement('span');
                badge.className = 'pb-filter-count';
                badge.textContent = String(n);
                ch.appendChild(badge);
            }
            ch.__pbwCat = cat;
            ch.addEventListener('click', function () { apply(cat); });
            bar.appendChild(ch);
            chips.push(ch);
        });
        apply('');
    }

    // ── Carte retournable au clic ────────────────────────────────────────
    // .pb-hovercard--click : la carte bascule (.is-flipped) au clic, à Entrée ou
    // Espace au lieu du survol — utilisable au doigt. Un lien dans la carte
    // garde son comportement normal.
    function initHoverCard(root) {
        if (!once(root)) return;
        if (!root.classList.contains('pb-hovercard--click')) return;
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '0');
        root.setAttribute('role', 'button');
        root.setAttribute('aria-pressed', 'false');
        function toggle() {
            var on = !root.classList.contains('is-flipped');
            root.classList.toggle('is-flipped', on);
            root.setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        root.addEventListener('click', function (e) {
            if (e.target.closest && e.target.closest('a[href]')) return;
            toggle();
        });
        root.addEventListener('keydown', function (e) {
            if (e.target !== root) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        });
    }

    // ── Compteur animé ───────────────────────────────────────────────────
    // La valeur finale (et son formatage : séparateurs, %, +, €…) vient du
    // DOM : on n'interpole que les chiffres et on garde le reste tel quel.
    function initCounter(root) {
        if (!once(root)) return;
        var out = root.querySelector('.pb-counter-value') || root;
        var finalText = (out.textContent || '').trim();
        var digits = finalText.replace(/[^\d]/g, '');
        if (!digits) return;
        var target = parseInt(digits, 10);
        if (!isFinite(target) || target <= 0) return;

        var duration = parseInt(root.getAttribute('data-pbw-duration') || '1400', 10);
        var groupSep = /\s| |,|\./.test(finalText.replace(/\d/g, '').trim()) ? finalText.replace(/[\d]/g, '').trim().charAt(0) : '';

        function render(value) {
            var s = String(value);
            if (groupSep && target >= 1000) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep === '.' ? '.' : groupSep === ',' ? ',' : ' ');
            // Réinjecte les chiffres dans le gabarit d'origine (préfixe/suffixe).
            out.textContent = finalText.replace(/[\d][\d\s., ]*/, s);
        }

        function run() {
            var t0 = 0;
            function step(ts) {
                if (!t0) t0 = ts;
                var p = Math.min(1, (ts - t0) / duration);
                // easeOutCubic : rapide puis freine, comme un compteur mécanique.
                render(Math.round(target * (1 - Math.pow(1 - p, 3))));
                if (p < 1) requestAnimationFrame(step);
                else out.textContent = finalText;
            }
            requestAnimationFrame(step);
        }

        whenVisible(root, function () { render(0); run(); });
    }

    // ── Barre de progression ─────────────────────────────────────────────
    function initProgress(root) {
        if (!once(root)) return;
        root.classList.add('pb-progress--live');
        var track = root.querySelector('.pb-progress-track');
        var bar = root.querySelector('.pb-progress-bar');
        if (track && bar) {
            track.setAttribute('role', 'progressbar');
            var pct = parseFloat((getComputedStyle(root).getPropertyValue('--pbw-value') || '').replace('%', ''));
            if (isFinite(pct)) {
                track.setAttribute('aria-valuenow', String(Math.round(pct)));
                track.setAttribute('aria-valuemin', '0');
                track.setAttribute('aria-valuemax', '100');
            }
        }
        whenVisible(root, function () { root.classList.add('pb-progress--in'); });
    }

    // ── Avant / après ────────────────────────────────────────────────────
    function initBeforeAfter(root) {
        if (!once(root)) return;
        var range = root.querySelector('.pb-ba-range');
        if (!range) return;

        function apply() {
            var v = Math.max(0, Math.min(100, parseFloat(range.value) || 0));
            root.style.setProperty('--pbw-split', v + '%');
        }
        range.addEventListener('input', apply);
        range.addEventListener('change', apply);
        apply();

        // Glisser directement sur l'image (le curseur reste le repli clavier) : même moteur que le bloc
        // « Comparaison d'images » (pd-compare.js) — le doigt ne bloque plus le défilement de la page.
        function setFromPct(p) {
            range.value = String(Math.max(0, Math.min(100, p)));
            apply();
        }
        if (window.PdCompare && window.PdCompare.track) {
            root.style.touchAction = 'pan-y pinch-zoom';
            window.PdCompare.track(root, { set: setFromPct });
            return;
        }
        function fromPointer(e) {
            var r = root.getBoundingClientRect();
            if (!r.width) return;
            setFromPct(((e.touches ? e.touches[0].clientX : e.clientX) - r.left) / r.width * 100);
        }
        var dragging = false;
        root.addEventListener('pointerdown', function (e) { dragging = true; fromPointer(e); });
        root.addEventListener('pointermove', function (e) { if (dragging) fromPointer(e); });
        window.addEventListener('pointerup', function () { dragging = false; });
    }

    // ── Bandeau de logos ─────────────────────────────────────────────────
    // L'animation défile jusqu'à -50 % : la piste doit donc contenir la série
    // deux fois pour boucler sans trou. Le doublon est fabriqué ici, jamais
    // enregistré — l'éditeur ne montre et ne stocke qu'une seule série.
    function initMarquee(root) {
        if (!once(root)) return;
        var track = root.querySelector('.pb-marquee-track');
        if (!track || track.children.length === 0) return;
        var clone = track.cloneNode(true);
        while (clone.firstChild) {
            var node = clone.firstChild;
            if (node.nodeType === 1) node.setAttribute('aria-hidden', 'true');
            track.appendChild(node);
        }
    }

    // ── Compte à rebours ─────────────────────────────────────────────────
    function initCountdown(root) {
        if (!once(root)) return;
        var target = Date.parse(root.getAttribute('data-pbw-until') || '');
        if (!isFinite(target)) return;

        var cells = {};
        ['days', 'hours', 'minutes', 'seconds'].forEach(function (u) {
            var el = root.querySelector('[data-pbw-unit="' + u + '"] .pb-countdown-num');
            if (el) cells[u] = el;
        });
        if (!Object.keys(cells).length) return;

        var overText = root.getAttribute('data-pbw-over') || '';
        var timer;

        function tick() {
            var left = target - Date.now();
            if (left <= 0) {
                clearInterval(timer);
                if (overText) {
                    var over = document.createElement('div');
                    over.className = 'pb-countdown-over';
                    over.textContent = overText;
                    root.replaceChildren(over);
                } else {
                    Object.keys(cells).forEach(function (u) { cells[u].textContent = '00'; });
                }
                return;
            }
            var s = Math.floor(left / 1000);
            var parts = {
                days: Math.floor(s / 86400),
                hours: Math.floor((s % 86400) / 3600),
                minutes: Math.floor((s % 3600) / 60),
                seconds: s % 60,
            };
            Object.keys(cells).forEach(function (u) {
                cells[u].textContent = (u === 'days' ? String(parts[u]) : String(parts[u]).padStart(2, '0'));
            });
        }
        tick();
        timer = setInterval(tick, 1000);
    }

    // ── Vidéo en fenêtre ─────────────────────────────────────────────────
    function initVideo(root) {
        if (!once(root)) return;
        var btn = root.querySelector('.pb-video-play') || root;
        var src = root.getAttribute('data-pbw-video') || '';
        if (!src) return;

        btn.addEventListener('click', function (e) {
            e.preventDefault();
            var modal = document.createElement('div');
            modal.className = 'pb-video-modal';
            modal.setAttribute('role', 'dialog');
            modal.setAttribute('aria-modal', 'true');

            var frame;
            if (/\.(mp4|webm|ogg)(\?|$)/i.test(src)) {
                frame = document.createElement('video');
                frame.setAttribute('controls', '');
                frame.setAttribute('autoplay', '');
                frame.src = src;
            } else {
                frame = document.createElement('iframe');
                frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture');
                frame.setAttribute('allowfullscreen', '');
                // YouTube refuse un embed sans Referer (« Erreur 153 ») : envoyer l'origine explicitement,
                // quelle que soit la politique du document.
                frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
                frame.src = src;
            }

            var close = document.createElement('button');
            close.type = 'button';
            close.className = 'pb-video-close';
            close.setAttribute('aria-label', root.getAttribute('data-pbw-close') || 'Fermer');
            close.textContent = '×';

            function dismiss() {
                document.removeEventListener('keydown', onKey);
                modal.remove();
                btn.focus();
            }
            function onKey(ev) { if (ev.key === 'Escape') dismiss(); }

            close.addEventListener('click', dismiss);
            modal.addEventListener('click', function (ev) { if (ev.target === modal) dismiss(); });
            document.addEventListener('keydown', onKey);

            modal.append(frame, close);
            document.body.appendChild(modal);
            close.focus();
        });
    }

    // ── Déclenchement à l'entrée dans l'écran ────────────────────────────
    var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            io.unobserve(en.target);
            var fn = en.target.__pbwOnVisible;
            if (fn) { en.target.__pbwOnVisible = null; fn(); }
        });
    }, { threshold: 0.25 }) : null;

    function whenVisible(el, fn) {
        if (!io) { fn(); return; }             // pas d'IO → effet immédiat, jamais rien de caché
        el.__pbwOnVisible = fn;
        io.observe(el);
    }

    var KINDS = [
        ['.pb-tabs', initTabs],
        ['.pb-counter', initCounter],
        ['.pb-progress', initProgress],
        ['.pb-ba', initBeforeAfter],
        ['.pb-marquee', initMarquee],
        ['.pb-countdown', initCountdown],
        ['.pb-video', initVideo],
        ['.pb-hotspots', initHotspots],
        ['.pb-filter', initFilter],
        ['.pb-hovercard', initHoverCard],
    ];

    function scan(scope) {
        if (!scope || !scope.querySelectorAll) return;
        KINDS.forEach(function (k) {
            if (scope.matches && scope.matches(k[0])) k[1](scope);
            var list = scope.querySelectorAll(k[0]);
            for (var i = 0; i < list.length; i++) k[1](list[i]);
        });
    }

    scan(document);

    var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
            var added = muts[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
                if (added[j].nodeType === 1) scan(added[j]);
            }
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });
})();
